import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Typography,
  SelectChangeEvent,
  IconButton,
  Slider,
  Paper,
} from '@mui/material';
import { Article } from '../../../types/article.types';
import { Website } from '../../../types/website.types'; // Import the Website type
import apiClient, { getWebsitesByPublisher } from 'api/apiClient';
import DeleteIcon from '@mui/icons-material/Delete'; // Import MUI Delete icon
import { API_ENDPOINTS } from 'api/endPoints';
import { Journalist } from '../../../types/journalist.types';
import JournalistForm from '../../journalists/components/JournalistForm';
import CollapsibleSection from '../../../components/common/CollapsibleSection';

interface ArticleFormProps {
  open: boolean;
  onClose: () => void;
  article?: Article;
  isEdit?: boolean;
  onSave: (data: Partial<Article>) => Promise<void>;
  publisherId?: string; // Add publisherId prop
}

const initialFormData: Partial<Article> = {
  headline: '',
  subheadline: '',
  content: '',
  publishDate: '',
  status: 'published',
  originalUrl: '',
  websitePK: '',
  journalistName: '',
  journalistPK: '',
  locationPK: '',
  imageUrls: [],
  tags: [],
  priority: 2, // Default to Medium
};

const fetchVectorStoreMetadata = async (id: string): Promise<Record<string, string> | null> => {
  try {
    const response = await apiClient.get(`/publisher/articles/${id}/attributes`);
    return response.data;
  } catch (error) {
    // Optionally handle 404 or 500 errors here
    return null;
  }
};

const ArticleForm: React.FC<ArticleFormProps> = ({ open, onClose, article, isEdit = false, onSave, publisherId }) => {
  const [formData, setFormData] = useState<Partial<Article>>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [websites, setWebsites] = useState<Website[]>([]); // Update the type of websites state
  const [metadata, setMetadata] = useState<{ name: string; value: string }[]>([]);
  const [metaName, setMetaName] = useState('');
  const [metaValue, setMetaValue] = useState('');
  // Journalists state
  const [journalists, setJournalists] = useState<Journalist[]>([]);
  const [isLoadingJournalists, setIsLoadingJournalists] = useState(false);
  const [journalistFormOpen, setJournalistFormOpen] = useState(false);
  const [journalistUpgradeMessage, setJournalistUpgradeMessage] = useState<string | undefined>(undefined);
  const [initialJournalistName, setInitialJournalistName] = useState<string | undefined>(undefined);
  const [upgradeHandled, setUpgradeHandled] = useState(false);

  // Reset form when dialog closes to prevent flash of old values when reopening
  useEffect(() => {
    if (!open) {
      setFormData(initialFormData);
      setMetadata([]);
      setErrors({});
      setMetaName('');
      setMetaValue('');
      setUpgradeHandled(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setErrors({}); // Clear errors when form is opened
      if (isEdit && article) {
        const formattedPublishDate = article.publishDate
          ? new Date(article.publishDate).toISOString().slice(0, 16) // Format to 'YYYY-MM-DDTHH:mm'
          : '';
        // Normalize priority ensuring it is 1|2|3
        const rawPriority = Number(article.priority);
        const normalizedPriority = (rawPriority === 1 || rawPriority === 2 || rawPriority === 3) ? (rawPriority as 1 | 2 | 3) : 2;
        
        // Normalize journalistPK: remove 'JOURNALIST#' prefix if present to match journalist.id
        let normalizedJournalistPK = article.journalistPK;
        if (normalizedJournalistPK && normalizedJournalistPK.startsWith('JOURNALIST#')) {
          normalizedJournalistPK = normalizedJournalistPK.replace('JOURNALIST#', '');
        }

        // Reset to initial form data first, then overlay article data to ensure empty fields are properly cleared
        setFormData({ 
          ...initialFormData, 
          ...article, 
          publishDate: formattedPublishDate, 
          priority: normalizedPriority,
          journalistPK: normalizedJournalistPK 
        });
        // Fetch vectorStoreMetadata if not present
        if (!article.vectorStoreMetadata && article.id) {
          fetchVectorStoreMetadata(article.id).then((metadataObj) => {
            if (metadataObj) {
              let metaArr = Object.entries(metadataObj).map(([name, value]) => ({ name, value }));
              // Backward compatibility: ensure 'id' is present
              if (article.id && !metaArr.some(m => m.name === 'id')) {
                metaArr = [{ name: 'id', value: article.id }, ...metaArr];
              }
              setMetadata(metaArr);
            }
          });
        }
      } else {
        // Set default publish date to last used or current date/time
        let formattedNow = '';
        const lastPublishDate = localStorage.getItem('lastPublishDate');
        if (lastPublishDate) {
          formattedNow = lastPublishDate;
        } else {
          const now = new Date();
          formattedNow = now.toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:mm'
        }
        setFormData({ ...initialFormData, publishDate: formattedNow, priority: 2 });
        setMetadata([]); // Reset metadata for new article
        setUpgradeHandled(false);
      }
    }
  }, [isEdit, article, open]);

  useEffect(() => {
    const fetchWebsites = async () => {
      if (!publisherId) return; // Ensure publisherId is defined
      try {
        const response = await getWebsitesByPublisher(publisherId);
        setWebsites(response);
        if (response.length === 1) {
          setFormData((prev) => ({ ...prev, websitePK: response[0].id }));
        }
      } catch (error) {
        console.error('Failed to fetch websites:', error);
      }
    };

    fetchWebsites();
  }, [publisherId]);

  // Fetch journalists for this publisher
  useEffect(() => {
    const fetchJournalists = async () => {
      if (!publisherId || !open) return;
      setIsLoadingJournalists(true);
      try {
        const resp = await apiClient.get(API_ENDPOINTS.PUBLISHER.JOURNALISTS.BY_PUBLISHER(publisherId));
        const list: Journalist[] = resp.data || [];
        setJournalists(list);
        // If there is exactly one journalist and none selected, do not auto-select (allow zero selection)
      } catch (e) {
        console.error('Failed to fetch journalists:', e);
      } finally {
        setIsLoadingJournalists(false);
      }
    };
    fetchJournalists();
  }, [publisherId, open]);

  // Upgrade path: if name exists but no PK, attempt to match or prompt to create
  useEffect(() => {
    if (!open || !isEdit || upgradeHandled) return;
    if (!publisherId) return;
    const name = article?.journalistName?.trim();
    const pk = article?.journalistPK?.trim();
    if (name && !pk) {
      // Try to find by name (case-insensitive)
      const tryMatch = async () => {
        try {
          const resp = await apiClient.get(API_ENDPOINTS.PUBLISHER.JOURNALISTS.BY_PUBLISHER(publisherId));
          const list: Journalist[] = resp.data || [];
          setJournalists(list);
          const match = list.find(j => j.name.trim().toLowerCase() === name.toLowerCase());
          if (match) {
            setFormData(prev => ({ ...prev, journalistPK: match.id, journalistName: match.name }));
            setUpgradeHandled(true);
          } else {
            // Open create modal prefilled
            setJournalistUpgradeMessage('This article is being upgraded to support linked Journalists. Please create the Journalist now to continue.');
            setInitialJournalistName(name);
            setJournalistFormOpen(true);
            setUpgradeHandled(true);
          }
        } catch (e) {
          console.error('Upgrade path check failed:', e);
        }
      };
      tryMatch();
    }
  }, [open, isEdit, article, publisherId, upgradeHandled]);

  // Initialize metadata for edit/new
  useEffect(() => {
    if (isEdit && article && article.vectorStoreMetadata) {
      let metaArr = Object.entries(article.vectorStoreMetadata).map(([name, value]) => ({ name, value }));
      // Backward compatibility: ensure 'id' is present
      if (article.id && !metaArr.some(m => m.name === 'id')) {
        metaArr = [{ name: 'id', value: article.id }, ...metaArr];
      }
      setMetadata(metaArr);
    } else if (!isEdit) {
      // For new article, always include id
      setMetadata(formData.id ? [{ name: 'id', value: formData.id }] : []);
    }
  }, [isEdit, article, formData.id]);

  useEffect(() => {
    if (!isEdit && websites.length === 1 && formData.websitePK !== websites[0].id) {
      setFormData((prev) => ({ ...prev, websitePK: websites[0].id }));
    }
  }, [websites, isEdit, formData.websitePK]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    setFormData({ ...formData, [name]: e.target.value });
  };

  const handleWebsiteChange = (e: SelectChangeEvent<string>) => {
    setFormData({ ...formData, websitePK: e.target.value });
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, tags: e.target.value.split(',').map(tag => tag.trim()) });
  };

  // Journalist selection change
  const handleJournalistChange = (e: SelectChangeEvent<string>) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setFormData(prev => ({ ...prev, journalistPK: '', journalistName: '' }));
      return;
    }
    const j = journalists.find(j => j.id === selectedId);
    setFormData(prev => ({ ...prev, journalistPK: selectedId, journalistName: j?.name || '' }));
  };

  const openAddJournalist = (upgradeMsg?: string, initialNameArg?: string) => {
    setJournalistUpgradeMessage(upgradeMsg);
    setInitialJournalistName(initialNameArg);
    setJournalistFormOpen(true);
  };

  const handleJournalistSaved = (saved: Journalist) => {
    // Ensure it's included in list and selected
    setJournalists(prev => {
      const exists = prev.some(j => j.id === saved.id);
      return exists ? prev.map(j => (j.id === saved.id ? saved : j)) : [saved, ...prev];
    });
    setFormData(prev => ({ ...prev, journalistPK: saved.id, journalistName: saved.name }));
    setJournalistUpgradeMessage(undefined);
    setInitialJournalistName(undefined);
  };

  // Metadata CRUD handlers
  const handleMetaChange = (index: number, field: 'name' | 'value', value: string) => {
    setMetadata((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddMeta = () => {
    if (!metaName.trim()) return;
    setMetadata((prev) => [...prev, { name: metaName.trim(), value: metaValue }]);
    setMetaName('');
    setMetaValue('');
  };

  const handleDeleteMeta = (index: number) => {
    // Prevent deleting 'id' for new articles
    if (!isEdit && metadata[index].name === 'id') return;
    setMetadata(metadata.filter((_, i) => i !== index));
  };

  const handleSave = async (closeAfter = true) => {
    setIsLoading(true);
    try {
      // Attach metadata to formData
      const metaObj = metadata.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.value }), {});
      // Normalize priority before saving (ensure number type 1|2|3)
      const rawPriority = Number(formData.priority);
      const normalizedPriority = (rawPriority === 1 || rawPriority === 2 || rawPriority === 3) ? (rawPriority as 1 | 2 | 3) : 2;
      await onSave({ ...formData, priority: normalizedPriority, vectorStoreMetadata: metaObj });
      // Save publish date to localStorage for next new article
      if (!isEdit && formData.publishDate) {
        localStorage.setItem('lastPublishDate', formData.publishDate);
      }
      if (closeAfter) {
        onClose();
      } else {
        // Reset form for new article
        let formattedNow = '';
        const lastPublishDate = localStorage.getItem('lastPublishDate');
        if (lastPublishDate) {
          formattedNow = lastPublishDate;
        } else {
          const now = new Date();
          formattedNow = now.toISOString().slice(0, 16);
        }
        setFormData({ ...initialFormData, publishDate: formattedNow, priority: 2 });
        setMetadata([]);
        setErrors({});
      }
    } catch (error) {
      setErrors({ form: 'Failed to save article.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Article' : 'New Article'}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Headline"
                name="headline"
                value={formData.headline}
                onChange={handleInputChange}
                error={!!errors['headline']}
                helperText={errors['headline']}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subheadline"
                name="subheadline"
                value={formData.subheadline}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </Grid>
            {/* Journalist selection */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 2 }}>
                {isLoadingJournalists ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading journalists…</Typography>
                  </Box>
                ) : journalists.length === 0 ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Journalist</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No journalists found for this publisher. Add your first journalist to link to this article.
                    </Typography>
                    <Button variant="outlined" onClick={() => openAddJournalist()}>
                      Add your first journalist
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel id="journalist-select-label" shrink>Journalist</InputLabel>
                      <Select
                        labelId="journalist-select-label"
                        name="journalistPK"
                        value={formData.journalistPK || ''}
                        onChange={handleJournalistChange}
                        label="Journalist"
                        displayEmpty
                        MenuProps={{
                          PaperProps: { style: { maxHeight: 48 * 5 + 8 } },
                        }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {journalists.map((j) => (
                          <MenuItem key={j.id} value={j.id}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: '100%' }}>
                              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.name}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.email || ''}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Select a journalist or choose "None" if the article has no associated journalist.
                      </Typography>
                      <Button size="small" onClick={() => setFormData(prev => ({ ...prev, journalistPK: '', journalistName: '' }))}>Clear</Button>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Can't find them?
                      </Typography>
                      <Button variant="text" onClick={() => openAddJournalist()}>
                        Add new journalist
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Original URL"
                name="originalUrl"
                value={formData.originalUrl}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={10}
                maxRows={10}
                label="Content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                disabled={isLoading}
                inputProps={{ style: { resize: 'vertical' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Publish Date"
                name="publishDate"
                type="datetime-local"
                value={formData.publishDate}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange}
                  disabled={isLoading}
                >
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="test">Test</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma separated)"
                name="tags"
                value={formData.tags?.join(', ') || ''}
                onChange={handleTagsChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>News Source</InputLabel>
                <Select
                  name="websitePK"
                  value={formData.websitePK || ''}
                  onChange={handleWebsiteChange}
                  disabled={isLoading || websites.length === 0}
                  MenuProps={{ PaperProps: { style: { maxHeight: 224 } } }} // Limit to 5 items before scrolling
                >
                  {websites.map((website) => (
                    <MenuItem key={website.id} value={website.id}>
                      {website.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Priority Slider - moved to bottom */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Priority: {formData.priority === 1 ? 'High' : formData.priority === 2 ? 'Medium' : 'Low'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '25px', ml: '30px' }}>
                <Box sx={{ flexBasis: '400px', minWidth: '400px' }}>
                  <Slider
                    min={1}
                    max={3}
                    step={1}
                    marks={[
                      { value: 1, label: 'High' },
                      { value: 2, label: 'Medium' },
                      { value: 3, label: 'Low' },
                    ]}
                    value={formData.priority ?? 2}
                    onChange={(_, value) => setFormData(prev => ({ ...prev, priority: value as 1 | 2 | 3 }))}
                    valueLabelDisplay="off"
                    disabled={isLoading}
                    sx={{ mt: 1 }}
                    aria-label="Article priority"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '280px', whiteSpace: 'nowrap' }}>
                  High priority (1) items may be surfaced or processed earlier.
                </Typography>
              </Box>
            </Grid>
            {/* Add more fields as needed */}
          </Grid>
          {errors.form && (
            <Typography color="error" sx={{ mt: 2 }}>{errors.form}</Typography>
          )}
          {/* Vector Store Metadata Section - wrapped in CollapsibleSection */}
          <CollapsibleSection 
            title="Vector Store Metadata" 
            defaultExpanded={false}
            id="vector-store-metadata"
            namespace="article-form"
          >
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              {formData.id && (
                <Typography variant="body2" color="text.secondary">fileId: <b>{formData.fileId}</b></Typography>
              )}
              {formData.vectorStoreId && (
                <Typography variant="body2" color="text.secondary">vectorStoreId: <b>{formData.vectorStoreId}</b></Typography>
              )}
            </Box>
            {/* Header row for Name/Value columns */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={3}>
                <Typography variant="subtitle2">Name</Typography>
              </Grid>
              <Grid item xs>
                <Typography variant="subtitle2">Value</Typography>
              </Grid>
              <Grid item xs={1} sx={{ minWidth: 40, maxWidth: 40 }} />
            </Grid>
            {/* Metadata rows */}
            {metadata.map((meta, idx) => (
              <Grid container spacing={2} alignItems="center" key={meta.name} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <TextField
                    value={meta.name}
                    onChange={e => handleMetaChange(idx, 'name', e.target.value)}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      style: { fontWeight: meta.name === 'id' ? 'bold' : undefined },
                    }}
                  />
                </Grid>
                <Grid item xs>
                  <TextField
                    value={meta.value}
                    onChange={e => handleMetaChange(idx, 'value', e.target.value)}
                    size="small"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={1} sx={{ minWidth: 40, maxWidth: 40, pl: 0, pr: 0 }}>
                  <IconButton color="error" onClick={() => handleDeleteMeta(idx)} disabled={!isEdit && meta.name === 'id'} size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                placeholder="Name"
                value={metaName}
                onChange={e => setMetaName(e.target.value)}
                size="small"
              />
              <TextField
                placeholder="Value"
                value={metaValue}
                onChange={e => setMetaValue(e.target.value)}
                size="small"
              />
              <Button variant="contained" color="primary" onClick={handleAddMeta}>Add</Button>
            </Box>
          </CollapsibleSection>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        {!isEdit && (
          <Button
            onClick={() => handleSave(false)}
            variant="outlined"
            color="primary"
            disabled={isLoading}
            sx={{ mr: 1 }}
          >
            Save & Add Another
          </Button>
        )}
        <Button
          onClick={() => handleSave(true)}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
      {/* Journalist creation modal */}
      <JournalistForm
        open={journalistFormOpen}
        onClose={() => setJournalistFormOpen(false)}
        isEdit={false}
        publisherId={publisherId}
        upgradeMessage={journalistUpgradeMessage}
        onSaved={handleJournalistSaved}
        initialName={initialJournalistName}
      />
    </Dialog>
  );
};

export default ArticleForm;
